import {
  ProcessWithTransitions,
  ProcessDefinition,
  ProcessValidationResult,
  ProcessNavigationItem,
  AddArtifactData,
  ProcessTransition,
} from '../types/processDefinition';
import { apiClient } from './apiClient';

export class ProcessDefinitionService {
  /**
   * Get a process with all its transitions (artifacts)
   */
  static async getProcessWithTransitions(processId: string): Promise<ProcessWithTransitions> {
    const response = await apiClient.get(`/prime-processes/${processId}/with-transitions`);
    return response.data;
  }

  /**
   * Get all transitions for a process, grouped by artifact
   */
  static async getProcessTransitions(processId: string) {
    const response = await apiClient.get(`/prime-processes/${processId}/transitions`);
    return response.data;
  }

  /**
   * Get validation results for a process
   */
  static async getProcessValidation(processId: string): Promise<ProcessValidationResult> {
    const response = await apiClient.get(`/prime-processes/${processId}/validation`);
    return response.data;
  }

  /**
   * Update a process
   */
  static async updateProcess(
    processId: string,
    processData: Partial<ProcessDefinition>
  ): Promise<ProcessWithTransitions> {
    const response = await apiClient.put(`/prime-processes/${processId}`, processData);
    return response.data;
  }

  /**
   * Add an artifact to a process (create a new transition)
   */
  static async addArtifactToProcess(
    processId: string,
    artifactData: AddArtifactData
  ): Promise<ProcessTransition> {
    const response = await apiClient.post('/prime-transitions', {
      prime_process_ref: processId,
      prime_artifact_ref: artifactData.artifact_id,
      from_state_ref: artifactData.input_state_id,
      to_state_ref: artifactData.output_state_id,
      default_transition_type: artifactData.role,
      transition_parameters: artifactData.transition_parameters || {},
    });
    return response.data;
  }

  /**
   * Update a transition
   */
  static async updateTransition(
    transitionId: string,
    transitionData: Partial<ProcessTransition>
  ): Promise<ProcessTransition> {
    const response = await apiClient.put(`/prime-transitions/${transitionId}`, transitionData);
    return response.data;
  }

  /**
   * Delete a transition
   */
  static async deleteTransition(transitionId: string): Promise<void> {
    await apiClient.delete(`/prime-transitions/${transitionId}`);
  }

  /**
   * Get processes that use a specific artifact/state combination
   */
  static async getProcessesUsingArtifactState(
    artifactId: string,
    stateId: string
  ): Promise<ProcessNavigationItem[]> {
    const response = await apiClient.get(
      `/prime-transitions/artifacts/${artifactId}/states/${stateId}/processes`
    );
    return response.data;
  }
}
